import {useEffect} from 'react';

import {getWorkspaceSettings} from '../../../features/workspaceSettings/store';
import {usePart} from '../../../state/views/hooks';
import {Ref as PanelBankConfigRef} from '../../../state/views/panelBankConfig/types';
import {Ref as PanelSettingsRef} from '../../../state/views/panelSettings/types';
import {useBetaFeatureWorkspaceOptimizations} from '../../../util/useBetaFeature';
import {useGorillaRunOutliers} from '../../PanelRunsLinePlot/outliers/useRunOutliers';
import {customWorkspaceUpdate, generateUpdates} from '../customWorkspaceEffect';

export function useInitWorkspaceSettings(
  panelBankConfigRef: PanelBankConfigRef,
  panelSettingsRef: PanelSettingsRef
) {
  const isCustomWorkspace = useBetaFeatureWorkspaceOptimizations();
  const hasGorillaRunsAccess = useGorillaRunOutliers();

  const panelBankConfigPart = usePart(panelBankConfigRef);
  const panelSettingsPart = usePart(panelSettingsRef);

  // We don't use the zustand setter functions elsewhere.
  // For right now zustand only reads values out of the stored settings,
  // which refreshes every time a new value gets written to it.
  const {reset, update} = getWorkspaceSettings(state => ({
    reset: state.reset,
    update: state.update,
  }));

  useEffect(() => {
    /**
     * because we don't have a stable ID on a workspace ref, we want to clear the slice of state
     * in zustand every time the workspace unmounts. It probably doesn't matter because the new
     * workspace values should overwrite in the effect, but trying to be safe
     */
    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    /**
     * Load all workspace settings into the zustand store for easier access
     * inside panels _without_ having to mess with context providers.
     */
    const updatesToApply = customWorkspaceUpdate(
      generateUpdates(
        panelBankConfigPart.settings,
        panelSettingsPart,
        isCustomWorkspace,
        hasGorillaRunsAccess
      ),
      isCustomWorkspace
    );

    if (Object.keys(updatesToApply).length > 0) {
      update(updatesToApply);
    }
  }, [
    panelBankConfigPart.settings,
    panelSettingsPart,
    hasGorillaRunsAccess,
    isCustomWorkspace,
    update,
  ]);
}
